import { useEffect, useMemo, useState } from 'react';

import adminApi from '@/lib/adminApi';

const emptyForm = {
  artist_id: '',
  artist_name: '',
  artist_profile_path: '',
  city: '',
  region_label: '',
  specialty: '',
  artwork_title: '',
  artwork_image_url: '',
  category_label: 'Curated Exhibit',
  description: '',
  status: 'published',
};

function FieldNote({ children }) {
  return <p className="mt-1 text-xs font-bold leading-5 text-[#6D28D9]">{children}</p>;
}

const exhibitionValue = (item, publicField, adminField, fallback = '') =>
  item?.[publicField] || item?.[adminField] || fallback;

const apiErrorMessage = (err, fallback, routeLabel = '') => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || err?.response?.data?.message;
  if (status === 404) {
    return `${fallback} Backend route ${routeLabel || 'for Art Exhibition'} is not live yet. Push/redeploy the backend, then refresh this page.`;
  }
  if (status === 401 || status === 403) {
    return `${fallback} Please login again as admin.`;
  }
  return detail || err?.message || fallback;
};

export default function AdminArtExhibition() {
  const [artists, setArtists] = useState([]);
  const [items, setItems] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const selectedArtistImages = useMemo(
    () => (Array.isArray(selectedArtist?.portfolio_image_urls) ? selectedArtist.portfolio_image_urls : []),
    [selectedArtist]
  );

  const loadArtists = async () => {
    const response = await adminApi.get('/admin/art-exhibition/artists', {
      params: { search: artistSearch },
    });
    setArtists(response.data?.artists || []);
  };

  const loadItems = async () => {
    const response = await adminApi.get('/admin/art-exhibition', {
      params: { search: itemSearch },
    });
    setItems(response.data?.items || []);
  };

  useEffect(() => {
    loadArtists().catch((err) => {
      setArtists([]);
      setError(apiErrorMessage(err, 'Could not load artists for exhibition.', '/api/admin/art-exhibition/artists'));
    });
    loadItems().catch((err) => setError(apiErrorMessage(err, 'Could not load exhibition records.', '/api/admin/art-exhibition')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const searchTimer = window.setTimeout(() => {
      loadArtists().catch((err) => {
        setArtists([]);
        setError(apiErrorMessage(err, 'Could not search artists for exhibition.', '/api/admin/art-exhibition/artists'));
      });
    }, 350);
    return () => window.clearTimeout(searchTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistSearch]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const selectArtist = (artist) => {
    const artistId = artist.id || artist.artist_id || '';
    const specialty = artist.display_category || artist.subcategory || artist.category || '';
    const profilePath = artist.artist_profile_path || artist.profile_path || artist.artist_profile_url || (artist.slug ? `/artist/${artist.slug}` : artistId ? `/artist/${artistId}` : '');
    setSelectedArtist(artist);
    setForm((current) => ({
      ...current,
      artist_id: artistId,
      artist_name: artist.name || '',
      artist_profile_path: profilePath,
      city: artist.city || '',
      region_label: artist.region || artist.state || artist.city || '',
      specialty,
      category_label: current.category_label === 'Curated Exhibit' ? specialty || 'Curated Exhibit' : current.category_label,
      artwork_image_url: current.artwork_image_url || artist.portfolio_image_urls?.[0] || artist.image_url || artist.profile_image_url || '',
    }));
  };

  const suggestArtworkText = () => {
    const specialty = form.specialty || form.category_label || 'Indian art';
    const city = form.city || form.region_label || 'India';
    const artistName = form.artist_name || 'KalaManch artist';
    setForm((current) => ({
      ...current,
      artwork_title: current.artwork_title || `${specialty} by ${artistName}`,
      description:
        current.description ||
        `A curated ${specialty.toLowerCase()} work from ${city}, presented on KalaManch Art Exhibition for public discovery.`,
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => updateForm('artwork_image_url', String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedArtist(null);
    setEditingId('');
    setMessage('');
    setError('');
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    setError('');
    try {
      if (editingId) {
        await adminApi.put(`/admin/art-exhibition/${editingId}`, form);
        setMessage('Exhibition artwork updated.');
      } else {
        await adminApi.post('/admin/art-exhibition', form);
        setMessage('Exhibition artwork added to the public gallery.');
      }
      await loadItems();
      resetForm();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save exhibition artwork.', '/api/admin/art-exhibition'));
    } finally {
      setBusy(false);
    }
  };

  const editItem = (item) => {
    setEditingId(item.id);
    setSelectedArtist(null);
    setForm({
      artist_id: item.artist_id || '',
      artist_name: item.artist_name || item.artistName || '',
      artist_profile_path: item.artist_profile_path || item.profilePath || '',
      city: item.city || '',
      region_label: item.region_label || item.regionLabel || '',
      specialty: item.specialty || '',
      artwork_title: item.artwork_title || item.title || '',
      artwork_image_url: item.artwork_image_url || item.image || '',
      category_label: item.category_label || item.specialty || 'Curated Exhibit',
      description: item.description || '',
      status: item.status || (item.isPublished ? 'published' : 'hidden'),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${exhibitionValue(item, 'title', 'artwork_title', 'this artwork')}" from the exhibition?`)) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/art-exhibition/${item.id}`);
      await loadItems();
      setMessage('Artwork deleted from exhibition.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete artwork.', '/api/admin/art-exhibition/{id}'));
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (item) => {
    const isPublished = item.isPublished || item.status === 'published';
    const action = isPublished ? 'unpublish' : 'publish';
    setBusy(true);
    try {
      await adminApi.post(`/admin/art-exhibition/${item.id}/${action}`);
      await loadItems();
      setMessage(isPublished ? 'Artwork hidden from public wall.' : 'Artwork published on public wall.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update publish status.', `/api/admin/art-exhibition/{id}/${action}`));
    } finally {
      setBusy(false);
    }
  };

  const moveItem = async (item, direction) => {
    const currentIndex = items.findIndex((candidate) => candidate.id === item.id);
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const next = [...items];
    [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
    setItems(next);
    try {
      await adminApi.post('/admin/art-exhibition/reorder', { item_ids: next.map((entry) => entry.id) });
      await loadItems();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save artwork order.', '/api/admin/art-exhibition/reorder'));
      await loadItems();
    }
  };

  return (
    <main className="space-y-6">
      <section className="rounded-[1.5rem] border border-orange-100 bg-white p-6 shadow-sm">
        <p className="text-xs font-black uppercase tracking-[0.35em] text-[#FF6F00]">Art Exhibition</p>
        <h1 className="mt-3 text-3xl font-black text-slate-950">Manage public gallery wall</h1>
        <p className="mt-3 max-w-4xl text-sm leading-7 text-slate-600">
          Add, edit, publish, hide, delete, and reorder artworks shown on the public Art Exhibition page.
          This is separate from Indian Art Stars.
        </p>
      </section>

      {message ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 font-bold text-emerald-700">{message}</div> : null}
      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 font-bold text-red-700">{error}</div> : null}

      <section className="grid gap-6 xl:grid-cols-[0.82fr_1.18fr]">
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.3em] text-[#4A148C]">Artists</p>
              <h2 className="mt-2 text-2xl font-black">Choose artist for this artwork</h2>
            </div>
            <button type="button" onClick={resetForm} className="btn-outline">New</button>
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              value={artistSearch}
              onChange={(event) => setArtistSearch(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3"
              placeholder="Type artist name, city, or art form"
            />
            <button type="button" onClick={loadArtists} className="btn-primary whitespace-nowrap">Refresh</button>
          </div>
          <FieldNote>Results update while you type. Selecting an artist auto-fills the artist name, city, and KalaManch profile link used on the public exhibition card.</FieldNote>
          <div className="mt-5 max-h-[34rem] space-y-3 overflow-auto pr-2">
            {artists.map((artist) => (
              <button
                key={artist.id || artist.artist_id}
                type="button"
                onClick={() => selectArtist(artist)}
                className={`w-full rounded-2xl border p-4 text-left transition ${
                  form.artist_id === (artist.id || artist.artist_id)
                    ? 'border-[#FF6F00] bg-orange-50'
                    : 'border-slate-100 bg-slate-50 hover:border-orange-200'
                }`}
              >
                <p className="font-black text-slate-950">{artist.name || 'Unnamed artist'}</p>
                <p className="mt-1 text-sm font-semibold text-slate-600">
                  {[artist.display_category || artist.subcategory || artist.category, artist.city].filter(Boolean).join(' - ')}
                </p>
              </button>
            ))}
            {artists.length === 0 ? <div className="rounded-2xl bg-slate-50 p-5 text-slate-500">No artists found.</div> : null}
          </div>
        </div>

        <form onSubmit={submitForm} className="rounded-[1.5rem] border border-emerald-100 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-emerald-700">Public exhibition artwork</p>
          <h2 className="mt-2 text-2xl font-black">{editingId ? 'Edit public exhibition artwork' : 'Add artwork to public exhibition'}</h2>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
            Fill these fields to control exactly what appears on the public Art Exhibition page: image, title, art form, artist name, city, and profile link.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Artist name</span>
              <input value={form.artist_name} onChange={(e) => updateForm('artist_name', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" required />
              <FieldNote>Name displayed below the artwork card.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Artist profile link</span>
              <input value={form.artist_profile_path} onChange={(e) => updateForm('artist_profile_path', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="/artist/name-art-city" />
              <FieldNote>Auto-filled when an artist is selected. Example: /artist/santosh-kumar-sandilya-painter-varanasi.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Artwork title</span>
              <input value={form.artwork_title} onChange={(e) => updateForm('artwork_title', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" required />
              <FieldNote>Short title such as Ganga Ghat Painting, Maa Durga Rangoli, Terracotta Birds.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Category label</span>
              <input value={form.category_label} onChange={(e) => updateForm('category_label', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" />
              <FieldNote>Small chip shown on the card, for example Rangoli Art, Pottery, Watercolor, or Curated Exhibit.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">City</span>
              <input value={form.city} onChange={(e) => updateForm('city', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" />
              <FieldNote>Primary city displayed below the artist name.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Region label</span>
              <input value={form.region_label} onChange={(e) => updateForm('region_label', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" />
              <FieldNote>State or region, for example Uttar Pradesh, Kashi, Rajasthan.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Status</span>
              <select value={form.status} onChange={(e) => updateForm('status', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3">
                <option value="published">Public on exhibition wall</option>
                <option value="hidden">Not visible publicly</option>
                <option value="draft">Draft / not visible publicly</option>
              </select>
              <FieldNote>Only published artworks appear on the public Art Exhibition page.</FieldNote>
            </label>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Use artist portfolio image</span>
              <select
                value=""
                onChange={(e) => e.target.value && updateForm('artwork_image_url', e.target.value)}
                className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3"
                disabled={selectedArtistImages.length === 0}
              >
                <option value="">Select existing artist image</option>
                {selectedArtistImages.map((url, index) => (
                  <option key={url} value={url}>Portfolio image {index + 1}</option>
                ))}
              </select>
              <FieldNote>Optional. Use an image already uploaded on the selected artist profile.</FieldNote>
            </label>
          </div>

          <label className="mt-4 block">
            <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Artwork image</span>
            <input value={form.artwork_image_url} onChange={(e) => updateForm('artwork_image_url', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Image URL or uploaded image data" required />
            <FieldNote>Use Upload artwork image below, or paste a public image URL.</FieldNote>
          </label>
          <div className="mt-3">
            <input type="file" accept="image/*" onChange={handleImageUpload} className="block w-full rounded-2xl border border-dashed border-orange-200 bg-orange-50 p-4 text-sm font-bold" />
          </div>

          <label className="mt-4 block">
            <span className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Short description</span>
            <textarea value={form.description} onChange={(e) => updateForm('description', e.target.value)} className="mt-2 min-h-28 w-full rounded-2xl border border-slate-200 px-4 py-3" />
            <FieldNote>Optional short note shown on the card and inside the artwork popup. Keep it concise.</FieldNote>
          </label>
          <button type="button" onClick={suggestArtworkText} className="mt-3 rounded-full border border-purple-200 px-5 py-3 text-sm font-black text-[#4A148C]">
            Suggest title and description
          </button>

          {form.artwork_image_url ? (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-3 text-xs font-black uppercase tracking-[0.22em] text-slate-500">Preview</p>
              <img src={form.artwork_image_url} alt="Artwork preview" className="max-h-72 rounded-xl object-contain" />
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-3">
            <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Saving...' : editingId ? 'Save artwork changes' : 'Add artwork to exhibition'}</button>
            <button type="button" onClick={resetForm} className="btn-outline">Clear</button>
          </div>
        </form>
      </section>

      <section className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.3em] text-[#FF6F00]">Current wall</p>
            <h2 className="mt-2 text-2xl font-black">Artworks in exhibition</h2>
          </div>
          <div className="flex gap-3">
            <input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Search wall" />
            <button type="button" onClick={loadItems} className="btn-outline">Search</button>
          </div>
        </div>
        <div className="mt-6 grid gap-4">
          {items.map((item, index) => (
            <article key={item.id} className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[7rem_1fr_auto]">
              <img
                src={exhibitionValue(item, 'image', 'artwork_image_url')}
                alt={exhibitionValue(item, 'title', 'artwork_title', 'Exhibition artwork')}
                className="h-28 w-28 rounded-2xl object-cover"
              />
              <div>
                <div className="flex flex-wrap gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-black ${item.isPublished || item.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                    {item.isPublished || item.status === 'published' ? 'Public on exhibition wall' : 'Not visible publicly'}
                  </span>
                  <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-[#C45A00]">
                    {exhibitionValue(item, 'specialty', 'category_label', 'Curated Exhibit')}
                  </span>
                </div>
                <h3 className="mt-3 text-xl font-black">{exhibitionValue(item, 'title', 'artwork_title', 'Untitled artwork')}</h3>
                <p className="font-bold text-[#4A148C]">{exhibitionValue(item, 'artistName', 'artist_name', 'KalaManch Artist')}</p>
                <p className="text-sm text-slate-500">
                  {[item.city, exhibitionValue(item, 'regionLabel', 'region_label')].filter(Boolean).join(' | ')}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                <button type="button" onClick={() => moveItem(item, -1)} disabled={index === 0} className="btn-outline disabled:opacity-40">Up</button>
                <button type="button" onClick={() => moveItem(item, 1)} disabled={index === items.length - 1} className="btn-outline disabled:opacity-40">Down</button>
                <button type="button" onClick={() => editItem(item)} className="btn-outline">Edit</button>
                <button type="button" onClick={() => togglePublish(item)} className="btn-outline">
                  {item.isPublished || item.status === 'published' ? 'Hide from wall' : 'Publish to wall'}
                </button>
                <button type="button" onClick={() => deleteItem(item)} className="rounded-full border border-red-200 px-5 py-3 font-black text-red-600">Delete</button>
              </div>
            </article>
          ))}
          {items.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">No exhibition artworks yet.</div> : null}
        </div>
      </section>
    </main>
  );
}
